import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CreditCard, FileText, AlertTriangle, CheckCircle2, DollarSign, Upload,
  Mail, RefreshCw, Shield, TrendingUp
} from 'lucide-react';

const CreditCollectionsTab = () => {
  const [selectedCustomer, setSelectedCustomer] = useState('1');

  const creditSummary = {
    limit: 500000,
    used: 320000,
    available: 180000,
    overdue30: 45000,
    overdue60: 22000,
    overdue90: 18000,
    onHold: false,
  };

  const agingInvoices = [
    { id: 'INV-2024-1201', customer: 'Al Rajhi Trading', amount: 25000, days: 35, bucket: '30' },
    { id: 'INV-2024-1198', customer: 'Gulf Distributors', amount: 20000, days: 62, bucket: '60' },
    { id: 'INV-2024-1180', customer: 'Saudi Foods Co', amount: 18000, days: 95, bucket: '90+' },
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Credit & Collections</h2>

      {/* Live Credit Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div whileHover={{ scale: 1.02 }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Credit limit</span>
            <CreditCard className="w-5 h-5 text-teal-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">SAR {(creditSummary.limit / 1000).toFixed(0)}K</p>
        </motion.div>
        <motion.div whileHover={{ scale: 1.02 }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Used</span>
            <TrendingUp className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">SAR {(creditSummary.used / 1000).toFixed(0)}K</p>
        </motion.div>
        <motion.div whileHover={{ scale: 1.02 }} className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Available</span>
            <CheckCircle2 className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-700">SAR {(creditSummary.available / 1000).toFixed(0)}K</p>
        </motion.div>
        <motion.div whileHover={{ scale: 1.02 }} className={`p-6 rounded-xl shadow-sm border ${creditSummary.onHold ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Status</span>
            {creditSummary.onHold ? <AlertTriangle className="w-5 h-5 text-red-600" /> : <Shield className="w-5 h-5 text-green-600" />}
          </div>
          <p className={`text-lg font-bold ${creditSummary.onHold ? 'text-red-700' : 'text-gray-900'}`}>
            {creditSummary.onHold ? 'Credit hold' : 'In good standing'}
          </p>
        </motion.div>
      </div>

      {/* Aging Invoices */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Aging invoices</h3>
          <span className="text-sm text-gray-500">30 / 60 / 90+ days overdue</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days overdue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bucket</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {agingInvoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{inv.id}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{inv.customer}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">SAR {inv.amount.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{inv.days}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      inv.bucket === '90+' ? 'bg-red-100 text-red-800' : inv.bucket === '60' ? 'bg-amber-100 text-amber-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {inv.bucket} days
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Workflow cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Log payment</h3>
          <p className="text-sm text-gray-600 mb-4">
            Record a customer payment (e.g. bank transfer). Upload receipt; the system creates a draft receipt in the Financial module. Once finance approves, credit is freed automatically.
          </p>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium">
            <Upload className="w-4 h-4" />
            Log payment & upload receipt
          </button>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">1-Click SOA</h3>
          <p className="text-sm text-gray-600 mb-4">
            Generate a company-branded Statement of Account (open + paid invoices) and email it to the customer's AP department.
          </p>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium">
            <Mail className="w-4 h-4" />
            Generate & email SOA
          </button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-900">Automated credit holds</p>
          <p className="text-sm text-amber-800">
            Orders are blocked from confirmation if the customer exceeds their credit limit or has overdue invoices, until finance clears the hold.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreditCollectionsTab;
